import { useState } from 'react'
import { cn } from '../../lib/cn'
import { formatCents } from '../../lib/clubMoney'
import { ClubButton } from './ClubButton'
import { EmptyPlate } from './EmptyPlate'
import type { StandingAttemptOutcome, StandingTeeTime, StandingTeeTimeBody } from '../../hooks/useStandingTeeTimes'
import type { GolfCourse } from '../../lib/types'

/**
 * Standing tee times — the Outings desk's recurring-order section
 * (design card: GTG GC — Standing Tee Times).
 *
 * The copy here does one job above all others: never let a member believe a
 * tee has been paid for. A standing order CLAIMS a tee as a proposal and the
 * member confirms it with their own card on the outing itself, so every string
 * on this surface says "claimed", never "booked", and the money plate states
 * the bounds in the member's own terms.
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const TOLERANCES = [15, 30, 60] as const

/**
 * A tee seats four. The operator sells starts in fours, the booking adapter
 * refuses more, and `StandingTeeTime` validates it server-side — this is the
 * picker refusing to offer what the server would reject. It must track the
 * desk's own `MAX_PARTY_SIZE` in OutingsScreen.tsx; they are the same fact.
 */
const MAX_PARTY = 4
const PARTY_SIZES = Array.from({ length: MAX_PARTY }, (_, i) => i + 1)

/**
 * Outcome vocabulary, in a member's words rather than the runner's.
 *
 * `tone` is a FORM, never a colour on its own: the design card reserves the
 * one flag red for the section's single CTA, so a failed run is told apart by
 * its marker and its sentence. That also means the log reads correctly for a
 * member who cannot separate the two greens.
 */
const OUTCOMES: Record<StandingAttemptOutcome, { label: string; marker: string }> = {
  claimed: { label: 'Claimed — awaiting your card', marker: 'bg-fairway' },
  booked: { label: 'Booked', marker: 'bg-fairway' },
  no_tee: { label: 'No bookable tee within tolerance', marker: 'border border-ink-soft/50' },
  over_ceiling: { label: 'Over your ceiling', marker: 'border border-brass-deep bg-brass/25' },
  refused: { label: 'The course could not be booked', marker: 'border border-brass-deep' },
  awaiting_member: { label: 'Waiting on tees you have already claimed', marker: 'border border-ink-soft/50' },
  sheet_unreadable: { label: 'Sheet unreadable — will retry', marker: 'border border-dashed border-brass-deep' },
}

const labelClass = 'mb-[7px] block text-xs font-semibold uppercase tracking-[0.16em] text-brass-deep'
const inputClass =
  'w-full rounded-club border border-ink/20 bg-parchment px-[13px] py-[9px] text-[14px] text-ink focus:border-fairway'

/** "Sat 30 Aug" from a plain YYYY-MM-DD — parsed as local, never as UTC. */
function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(y, m - 1, d))
}

function formatTee(iso: string | null): string {
  if (!iso) return '—'
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(at)
}

function OrderChip({ status }: { status: StandingTeeTime['status'] }) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-club-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]',
        status === 'active' ? 'border-fairway bg-sage-soft text-fairway' : 'border-brass text-brass-deep',
      )}
    >
      {status}
    </span>
  )
}

/** The per-date log. Absent until a run has actually happened. */
function AttemptLog({ rule }: { rule: StandingTeeTime }) {
  if (rule.attempts.length === 0) {
    return (
      <p className="mt-3 text-[13px] text-ink-soft">
        Nothing yet — this order looks each time a matching sheet opens.
      </p>
    )
  }

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[420px] border-collapse text-left text-[13px]">
        <thead>
          <tr className="border-b border-brass/45">
            {['Date', 'Course', 'Tee', 'A head', 'What happened'].map((head) => (
              <th
                key={head}
                scope="col"
                className="py-2 pr-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-brass-deep"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rule.attempts.map((attempt) => (
            <tr key={attempt.id} className="border-b border-ink/10 align-top">
              <td className="py-2 pr-3 whitespace-nowrap text-ink">{formatDay(attempt.target_date)}</td>
              <td className="py-2 pr-3 text-ink-soft">{attempt.course_name ?? '—'}</td>
              <td className="py-2 pr-3 whitespace-nowrap text-ink-soft">
                {formatTee(attempt.slot_tee_off_at)}
              </td>
              <td className="py-2 pr-3 whitespace-nowrap text-ink-soft">
                {attempt.price_cents_per_head == null ? '—' : formatCents(attempt.price_cents_per_head)}
              </td>
              <td className="py-2 text-ink-soft">
                <span className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className={cn('mt-[5px] h-2 w-2 shrink-0', OUTCOMES[attempt.outcome].marker)}
                  />
                  <span>
                    <span className="text-ink">{OUTCOMES[attempt.outcome].label}</span>
                    {attempt.detail && <span className="block text-[12.5px]">{attempt.detail}</span>}
                  </span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function OrderCard({
  rule,
  onPause,
  onResume,
  onDelete,
}: {
  rule: StandingTeeTime
  onPause: (id: string) => Promise<void>
  onResume: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)

  const run = async (action: (id: string) => Promise<void>) => {
    setBusy(true)
    try {
      await action(rule.id)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="rounded-club-lg border border-brass/45 bg-parchment p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-display text-[19px] font-medium text-pine">
            {DAYS[rule.weekday]}s · {rule.target_clock} ± {rule.tolerance_minutes} min
          </h4>
          <p className="mt-1 text-[13.5px] text-ink-soft">{rule.course_names.join(', ')}</p>
          <p className="mt-1 text-[13.5px] text-ink-soft">
            {rule.slots} {rule.slots === 1 ? 'player' : 'players'} · {rule.holes} holes · up to{' '}
            {formatCents(rule.max_price_cents_per_head)} a head
          </p>
        </div>
        <OrderChip status={rule.status} />
      </div>

      {rule.paused_reason && (
        <p className="mt-3 text-[13px] text-ink">{rule.paused_reason}</p>
      )}

      <AttemptLog rule={rule} />

      <div className="mt-4 flex flex-wrap gap-3">
        {rule.status === 'active' ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(onPause)}
            className="text-[13px] text-fairway underline underline-offset-[3px] disabled:opacity-50"
          >
            Pause
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(onResume)}
            className="text-[13px] text-fairway underline underline-offset-[3px] disabled:opacity-50"
          >
            Resume
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(onDelete)}
          className="text-[13px] text-ink-soft underline underline-offset-[3px] hover:text-flag disabled:opacity-50"
        >
          Delete
        </button>
      </div>
    </li>
  )
}

/** The composer. One flag CTA on the whole section, per the design card. */
function OrderComposer({
  courses,
  onCreate,
}: {
  courses: GolfCourse[]
  onCreate: (body: StandingTeeTimeBody) => Promise<unknown>
}) {
  const [weekday, setWeekday] = useState(6)
  const [time, setTime] = useState('07:30')
  const [tolerance, setTolerance] = useState<number>(30)
  const [courseKeys, setCourseKeys] = useState<string[]>([])
  const [slots, setSlots] = useState(4)
  const [holes, setHoles] = useState(18)
  const [ceiling, setCeiling] = useState('40')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const toggleCourse = (key: string) => {
    setCourseKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : prev.length >= 3 ? prev : [...prev, key],
    )
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const [hours, minutes] = time.split(':').map(Number)
    const dollars = Number(ceiling)
    if (courseKeys.length === 0) return setError('Name at least one course.')
    if (!Number.isFinite(dollars) || dollars <= 0) return setError('Set what you are willing to pay.')

    setBusy(true)
    setError(null)
    try {
      await onCreate({
        weekday,
        target_minutes: hours * 60 + minutes,
        tolerance_minutes: tolerance,
        course_keys: courseKeys,
        slots,
        holes,
        max_price_cents_per_head: Math.round(dollars * 100),
      })
      setCourseKeys([])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set that standing order.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="rounded-club-lg border border-brass/45 bg-parchment p-5">
      <p className={labelClass}>A standing order</p>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="standing-weekday" className={labelClass}>
            Day
          </label>
          <select
            id="standing-weekday"
            className={inputClass}
            value={weekday}
            onChange={(e) => setWeekday(Number(e.target.value))}
          >
            {DAYS.map((day, index) => (
              <option key={day} value={index}>
                {day}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="standing-time" className={labelClass}>
            Around
          </label>
          <input
            id="standing-time"
            type="time"
            className={inputClass}
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="standing-tolerance" className={labelClass}>
            Give or take
          </label>
          <select
            id="standing-tolerance"
            className={inputClass}
            value={tolerance}
            onChange={(e) => setTolerance(Number(e.target.value))}
          >
            {TOLERANCES.map((minutes) => (
              <option key={minutes} value={minutes}>
                ± {minutes} min
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="standing-slots" className={labelClass}>
            Players
          </label>
          <select
            id="standing-slots"
            className={inputClass}
            value={slots}
            onChange={(e) => setSlots(Number(e.target.value))}
          >
            {PARTY_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="standing-holes" className={labelClass}>
            Holes
          </label>
          <select
            id="standing-holes"
            className={inputClass}
            value={holes}
            onChange={(e) => setHoles(Number(e.target.value))}
          >
            <option value={18}>18</option>
            <option value={9}>9</option>
          </select>
        </div>
        <div>
          <label htmlFor="standing-ceiling" className={labelClass}>
            Most a head
          </label>
          <input
            id="standing-ceiling"
            type="number"
            min="1"
            step="1"
            className={inputClass}
            value={ceiling}
            onChange={(e) => setCeiling(e.target.value)}
          />
        </div>
      </div>

      <fieldset className="mt-4">
        <legend className={labelClass}>Courses, in order of preference</legend>
        <div className="flex flex-wrap gap-2">
          {courses.map((course) => {
            const rank = courseKeys.indexOf(course.key)
            return (
              <button
                key={course.key}
                type="button"
                aria-pressed={rank >= 0}
                onClick={() => toggleCourse(course.key)}
                className={cn(
                  'rounded-club-sm border px-2.5 py-1 text-[12.5px]',
                  rank >= 0
                    ? 'border-fairway bg-sage-soft text-fairway'
                    : 'border-ink/20 text-ink-soft hover:border-fairway',
                )}
              >
                {rank >= 0 && <span className="mr-1 font-semibold text-brass-deep">{rank + 1}</span>}
                {course.name}
              </button>
            )
          })}
        </div>
        <p className="mt-2 text-[12.5px] text-ink-soft">
          Up to three. A course whose sheet opens later than the others holds the
          whole order back, so keep them on the same booking window if you can.
        </p>
      </fieldset>

      {error && (
        <p role="alert" className="mt-3 text-[13px] text-flag">
          {error}
        </p>
      )}

      <ClubButton type="submit" size="sm" disabled={busy} className="mt-4">
        {busy ? 'Setting…' : 'Set the standing order'}
      </ClubButton>
    </form>
  )
}

export function StandingTeeTimes({
  courses,
  rules,
  runnerEnabled,
  isLoading,
  error,
  onCreate,
  onPause,
  onResume,
  onDelete,
}: {
  courses: GolfCourse[]
  rules: StandingTeeTime[]
  runnerEnabled: boolean
  isLoading: boolean
  error: string | null
  onCreate: (body: StandingTeeTimeBody) => Promise<unknown>
  onPause: (id: string) => Promise<void>
  onResume: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}) {
  return (
    <div className="flex flex-wrap items-start gap-8">
      <div className="min-w-0 flex-[1_1_420px]">
        {isLoading && <p className="text-[13.5px] text-ink-soft">Reading your standing orders…</p>}
        {error && (
          <p role="alert" className="text-[13.5px] text-flag">
            {error}
          </p>
        )}

        {/* Said before the list, not after it: an order that exists in an
            environment whose runner is switched off will never fire, and a
            member must not be left to work that out from an empty log. */}
        {!isLoading && !runnerEnabled && rules.length > 0 && (
          <p className="mb-4 rounded-club border border-brass/45 bg-paper-deep px-4 py-3 text-[13px] text-ink">
            Standing orders are not running here yet. Yours are saved and will
            start looking as soon as the club switches them on.
          </p>
        )}

        {!isLoading && !error && rules.length === 0 ? (
          <EmptyPlate
            title="No standing orders."
            description="Set one and the club watches the sheet for you — it claims a matching tee the moment the course opens the day, and asks you to confirm it."
          />
        ) : (
          <ul className="flex flex-col gap-4">
            {rules.map((rule) => (
              <OrderCard
                key={rule.id}
                rule={rule}
                onPause={onPause}
                onResume={onResume}
                onDelete={onDelete}
              />
            ))}
          </ul>
        )}

        {/* The money plate. States the bounds in the member's own terms, and
            says the one thing that must never be ambiguous. */}
        <div className="mt-6 rounded-club-lg border border-brass/45 bg-paper-deep p-5">
          <p className={labelClass}>What a standing order can and cannot do</p>
          <p className="mt-2 text-[14px] text-ink">
            A standing order never spends. It holds the tee as a proposal; you
            type the card.
          </p>
          <ul className="mt-3 flex flex-col gap-1.5 text-[13px] text-ink-soft">
            <li>It will not claim a tee over the ceiling you set.</li>
            <li>It pauses itself after four runs that find nothing.</li>
            <li>It claims each date once, however many times it looks.</li>
            <li>Pause or delete it at any time — both take effect immediately.</li>
          </ul>
        </div>
      </div>

      <aside className="flex-[0_0_clamp(230px,28vw,330px)] max-[860px]:flex-[1_1_100%]">
        <OrderComposer courses={courses} onCreate={onCreate} />
      </aside>
    </div>
  )
}
